import {HttpErrorResponse} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {ErrorHandlerUtils} from '@app/core/utils/error-handler.utils';
import {ToastMessageUtils} from '@app/core/utils/toast-message.utils';
import {EActionStatus} from '@app/shared/enums/action-status.enum';
import {EToastMessageOptions, EToastMessageSeverity} from '@app/shared/enums/toast-message.enum';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {TranslateService} from '@ngx-translate/core';
import {MessageService} from 'primeng/api';
import {catchError, map, of, switchMap} from 'rxjs';
import {IEditAccountDetails} from '../models/accounts.model';
import {ICustomerResponse} from '../models/customer-response.model';
import {AccountsService} from '../services/accounts.service';
import {
  AccountsActions,
  customersAction,
  customersEffectError,
  customersEffectFailure,
  customersEffectSuccess,
  loadEditCustomerAccountDetails,
  loadEditCustomerAccountDetailsFailure,
  loadEditCustomerAccountDetailsSuccess,
  loadRolesFailure,
  loadRolesSuccess,
  saveAccount,
  saveAccountFailure,
  saveAccountSuccess,
  updateAccount,
  updateAccountFailure,
  updateAccountSuccess,
  updateOrganization,
  updateOrganizationFailure,
  updateOrganizationSuccess,
} from './accounts.actions';

@Injectable()
export class AccountsEffects {
  private readonly actions$ = inject(Actions);
  private readonly accountsService = inject(AccountsService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  globalToast = 'global-toast';

  saveAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountsActions.SaveAccount),
      switchMap((action: ReturnType<typeof saveAccount>) => this.saveAccount(action))
    )
  );

  updateAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountsActions.UpdateAccount),
      switchMap((action: ReturnType<typeof updateAccount>) => this.updateAccount(action))
    )
  );

  loadEditCustomerAccountDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountsActions.LoadEditCustomerAccountDetails),
      switchMap((action: ReturnType<typeof loadEditCustomerAccountDetails>) =>
        this.loadEditCustomerAccountDetails(action)
      )
    )
  );

  customers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountsActions.Customers),
      switchMap((action: ReturnType<typeof customersAction>) => this.customers(action))
    )
  );

  private customers(action: ReturnType<typeof customersAction>) {
    return this.accountsService.getCustomers(action.request).pipe(
      map((response: ICustomerResponse) => {
        return customersEffectSuccess({response});
      }),
      catchError((error: HttpErrorResponse) => {
        const errMsg = ErrorHandlerUtils.getErrorMessage(this.translate, error.status);
        ErrorHandlerUtils.showErrorMessage(this.messageService, this.globalToast, errMsg);
        if (Array.isArray(error.error)) {
          return of(customersEffectFailure({reason: error.error.join(', ')}));
        } else if (typeof error.error === 'string') {
          return of(customersEffectFailure({reason: error.error}));
        } else {
          return of(customersEffectError(error));
        }
      })
    );
  }

  loadRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountsActions.LoadRoles),
      switchMap(() => this.loadRoles())
    )
  );

  private loadRoles() {
    return this.accountsService.getRoles().pipe(
      map(roles => loadRolesSuccess({roles: roles})),
      catchError(() => of(loadRolesFailure()))
    );
  }

  updateOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountsActions.UpdateOrganization),
      switchMap((action: ReturnType<typeof updateOrganization>) => this.updateOrganization(action))
    )
  );

  private updateOrganization(action: ReturnType<typeof updateOrganization>) {
    return this.accountsService.updateCustomerOrganizationStatus(action.payload).pipe(
      switchMap(() => {
        ToastMessageUtils.showMessage({
          messageService: this.messageService,
          toastKey: this.globalToast,
          severity: EToastMessageSeverity.Success,
          messageDetail: this.translate.instant('Accounts.UpdateOrganizationSuccess'),
          closable: true,
          life: EToastMessageOptions.LifeTime,
        });
        return of(updateOrganizationSuccess({status: EActionStatus.Success}));
      }),
      catchError((error: HttpErrorResponse) => {
        const errMsg = ErrorHandlerUtils.getErrorMessage(this.translate, error.status);
        ToastMessageUtils.showMessage({
          messageService: this.messageService,
          toastKey: this.globalToast,
          severity: EToastMessageSeverity.Error,
          messageDetail: errMsg,
          closable: true,
          life: EToastMessageOptions.LifeTime,
        });

        return of(updateOrganizationFailure());
      })
    );
  }

  private saveAccount(action: ReturnType<typeof saveAccount>) {
    return this.accountsService.saveAccount(action.payload).pipe(
      switchMap(() => {
        ToastMessageUtils.showMessage({
          messageService: this.messageService,
          toastKey: this.globalToast,
          severity: EToastMessageSeverity.Success,
          messageDetail: this.translate.instant('Accounts.AddAccountSuccess'),
          closable: true,
          life: EToastMessageOptions.LifeTime,
        });
        return of(saveAccountSuccess({status: EActionStatus.Success}));
      }),
      catchError((error: HttpErrorResponse) => {
        const errMsg = ErrorHandlerUtils.getErrorMessage(this.translate, error.status);
        ToastMessageUtils.showMessage({
          messageService: this.messageService,
          toastKey: this.globalToast,
          severity: EToastMessageSeverity.Error,
          messageDetail: errMsg,
          closable: true,
          life: EToastMessageOptions.LifeTime,
        });

        return of(saveAccountFailure({status: EActionStatus.Failure}));
      })
    );
  }

  private updateAccount(action: ReturnType<typeof updateAccount>) {
    return this.accountsService.updateAccount(action.payload).pipe(
      switchMap(() => {
        ToastMessageUtils.showMessage({
          messageService: this.messageService,
          toastKey: this.globalToast,
          severity: EToastMessageSeverity.Success,
          messageDetail: this.translate.instant('Accounts.UpdateAccountSuccess'),
          closable: true,
          life: EToastMessageOptions.LifeTime,
        });
        return of(updateAccountSuccess({status: EActionStatus.Success}));
      }),
      catchError((error: HttpErrorResponse) => {
        const errMsg = ErrorHandlerUtils.getErrorMessage(this.translate, error.status);
        ToastMessageUtils.showMessage({
          messageService: this.messageService,
          toastKey: this.globalToast,
          severity: EToastMessageSeverity.Error,
          messageDetail: errMsg,
          closable: true,
          life: EToastMessageOptions.LifeTime,
        });

        return of(updateAccountFailure({status: EActionStatus.Failure}));
      })
    );
  }

  private loadEditCustomerAccountDetails(action: ReturnType<typeof loadEditCustomerAccountDetails>) {
    return this.accountsService.getEditAccountDetails(parseInt(action.customerId), action.organizationId).pipe(
      switchMap((response: IEditAccountDetails) => {
        return of(
          loadEditCustomerAccountDetailsSuccess({
            editCustomerAccountInfo: response,
          })
        );
      }),
      catchError((error: HttpErrorResponse) => {
        const errMsg = ErrorHandlerUtils.getErrorMessage(this.translate, error.status);
        ToastMessageUtils.showMessage({
          messageService: this.messageService,
          toastKey: this.globalToast,
          severity: EToastMessageSeverity.Error,
          messageDetail: errMsg,
          closable: true,
          life: EToastMessageOptions.LifeTime,
        });

        return of(loadEditCustomerAccountDetailsFailure());
      })
    );
  }
}
